import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import { useLocation } from 'react-router'
import authProvider from '../../core/AuthenticationProvider'
import { Element } from '../common/html/element'
import { Image } from '../common/image'
import { getInnerWidth } from '../helpers/device'
import { responsive } from '../utils/layout'
import './Chatbot.scss'
import chatbotIcon from './chatbot.svg'
import ContentChat from './component/ContentChat'
import FooterChatbot from './component/FooterChatbot'
import HeaderChatbot from './component/HeaderChatbot'
import HistoryChatbot from './component/HistoryChatbot'
import PendingResponseBubble from './component/PendingResponseBubble'
import QuestionSuggest from './component/QuestionSuggest'
import TextActionBubble from './component/TextActionBubble'
import { STATUS_MESS, USER_TYPE_CODE } from './constants'
import SignalRService from './core/SignalRService'
import './lang/i18n'
import {
  addMessageInTopic,
  changeShowHistoryList,
  changeTopicActive,
  changeTyping,
  changStatusMess,
  selectMessageTopic,
  selectStatusMess,
  selectTopicActive,
  updateBotMessage,
} from './store/slice'
import { getQuestion, getTopicMaxUpdatedDate } from './store/thunk'

const Chatbot = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const params = new Proxy(new URLSearchParams(location.search), {
    get: (searchParam, prop) => searchParam.get(prop),
  })
  const locale = useSelector((state) => state.i18n.locale)

  const messageTopic = useSelector(selectMessageTopic)
  const statusMess = useSelector(selectStatusMess)
  const topicActive = useSelector(selectTopicActive)
  const [isOpen, setIsOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const [questionReply, setQuestionReply] = useState('')
  const [isChangeLayout, setIsChangeLayout] = useState(false)

  const popupRef = useRef(null)
  const chatbotBodyRef = useRef(null)
  const messageInputRef = useRef(null)
  const activeTimersRef = useRef([])
  const messagesScrollRef = useRef(null)
  const currentBotMessageIdRef = useRef(null)
  const userMessageTimestampRef = useRef(null)

  const toggleChatbot = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      dispatch(changeShowHistoryList(false))
      dispatch(
        changeTyping({
          messageId: null,
          done: true,
        }),
      )
    }
  }

  useEffect(() => {
    authProvider.getUser().then((user) => {
      const userId = user?.profile?.user_id
      dispatch(
        getQuestion({
          UserId: userId,
          ProductId: 1, // 1: FiinGate
        }),
      )
    })
  }, [locale])

  // Cleanup any streaming timers on unmount
  useEffect(() => {
    dispatch(getTopicMaxUpdatedDate({}))
    return () => {
      activeTimersRef.current.forEach((t) => clearInterval(t))
      activeTimersRef.current = []
    }
  }, [])

  // Register callbacks once; connect lazily on send
  useEffect(() => {
    // Setup callbacks
    SignalRService.onAck((ack) => {
      dispatch(
        changeTopicActive({
          topicId: ack?.topicId,
          topicTitle: ack?.topicTitle,
        }),
      )
      dispatch(changStatusMess(STATUS_MESS.PENDING))
    })

    SignalRService.onReplyMsg((replyMsg) => {
      dispatch(
        changeTopicActive({
          topicId: replyMsg?.topicId,
          topicTitle: replyMsg?.topicTitle,
        }),
      )
      dispatch(changStatusMess(STATUS_MESS.PROCESSING))
      if (!currentBotMessageIdRef.current) {
        // Log response time
        if (userMessageTimestampRef.current) {
          const responseTime = Date.now() - userMessageTimestampRef.current
          console.log(
            `[Chatbot] Response time: ${(responseTime / 1000).toFixed(2)}s`,
          )
          console.log(
            `[Chatbot] User sent at: ${new Date(
              userMessageTimestampRef.current,
            ).toLocaleTimeString()}`,
          )
          console.log(
            `[Chatbot] Bot replied at: ${new Date().toLocaleTimeString()}`,
          )
          userMessageTimestampRef.current = null
        }
        const aiMessageId = Date.now()
        currentBotMessageIdRef.current = aiMessageId
        dispatch(
          changeTyping({
            messageId: aiMessageId,
            done: false,
          }),
        )
        dispatch(
          addMessageInTopic({
            messageId: aiMessageId,
            userTypeCode: USER_TYPE_CODE.BOT,
            content: replyMsg.msgText, // đoạn đầu tiên
          }),
        )
      } else {
        // Update message BOT đang stream bằng cách nối thêm text
        dispatch(
          updateBotMessage({
            messageId: currentBotMessageIdRef.current,
            content: replyMsg.msgText,
          }),
        )
      }
      // const aiMessageId = Date.now()
      // dispatch(
      //   addMessageInTopic({
      //     messageId: aiMessageId,
      //     userTypeCode: USER_TYPE_CODE.BOT,
      //     content: replyMsg.msgText,
      //   }),
      // )
    })

    SignalRService.onReplyCompleted((replyMsg) => {
      currentBotMessageIdRef.current = null
      dispatch(
        changeTopicActive({
          topicId: replyMsg?.topicId,
          topicTitle: replyMsg?.topicTitle,
        }),
      )
    })

    SignalRService.onError((error) => {
      console.error('SignalR Error:', error)
    })

    return () => {
      SignalRService.disconnect()
    }
  }, [])

  const sendUserMessage = async (userContent, askText) => {
    if (!userContent || !userContent.trim()) return

    const { isConnected } = SignalRService.getConnectionStatus()
    if (!isConnected) {
      const userInfo = await authProvider.getUser()
      const connected = await SignalRService.initializeConnection(
        userInfo?.profile?.user_id,
      )
      if (!connected) {
        const errorMessage = {
          messageId: Date.now() + 1,
          userTypeCode: 'system',
          content: I18n.t('chatbot.CONNECTION_ERROR'),
          isError: true,
        }
        dispatch(addMessageInTopic(errorMessage))
        return
      }
    }

    // Track timestamp when user sends message
    const timestamp = Date.now()
    userMessageTimestampRef.current = timestamp
    console.log(
      `[Chatbot] User sent message at: ${new Date(
        timestamp,
      ).toLocaleTimeString()}`,
    )

    const userMessage = {
      messageId: timestamp,
      userTypeCode: USER_TYPE_CODE.USER,
      content: userContent,
      ...(askText ? { ask: askText } : {}),
    }
    dispatch(addMessageInTopic(userMessage))

    // Set status to PENDING with a slight delay for better UX
    setTimeout(() => {
      dispatch(changStatusMess(STATUS_MESS.PENDING))
    }, 400)

    setQuestion('')
    setQuestionReply('')
    // Scroll to bottom only once right after sending
    setTimeout(() => {
      try {
        messagesScrollRef.current && messagesScrollRef.current.scrollToBottom()
      } catch (_) {}
    }, 0)

    // Gửi tin nhắn qua SignalR
    const messageData = {
      topicId: topicActive?.topicId,
      messageText: userContent,
      languageCode: 'vi',
      quotedMessageId: askText ? 2 : null,
      quoteText: askText || null,
      quoteUrl: null,
      productId: 1, // 1: FiinGate
      organirationId: params.organizationId
        ? Number(params.organizationId)
        : null,
    }
    const success = await SignalRService.sendUserMessage(messageData)
    if (success) {
    } else {
      // Nếu gửi thất bại, hiển thị tin nhắn lỗi
      const errorMessage = {
        messageId: Date.now() + 1,
        userTypeCode: USER_TYPE_CODE.SYSTEM,
        content: I18n.t('chatbot.SEND_MESSAGE_ERROR'),
        isError: true,
      }
      dispatch(addMessageInTopic(errorMessage))
      // Reset status on error
      dispatch(changStatusMess(STATUS_MESS.ACTIVE))
    }
  }

  useEffect(() => {
    setQuestionReply(null)
  }, [topicActive])

  useEffect(() => {
    setTimeout(() => {
      messagesScrollRef.current?.scrollToBottom()
    }, 100)
  }, [isOpen, topicActive])

  useEffect(() => {
    const resize = () => {
      if (getInnerWidth() < responsive.PIVOT_WIDTH) {
        setIsChangeLayout(true)
      } else {
        setIsChangeLayout(false)
      }
    }

    resize()
    window.addEventListener('resize', resize)

    return () => window.removeEventListener('resize', resize)
  }, [])

  return (
    <div className="chatbot-container">
      {/* Chatbot Icon Button */}
      {!isOpen && (
        <div className="chatbot-icon" onClick={toggleChatbot}>
          <Image
            src={chatbotIcon}
            className="chatbot-img"
            alt="logo"
            style={{ width: 36, height: 36 }}
          />
          <Element style={{ fontSize: 8 }} className="chatbot-text">
            <Translate value="chatbot.FIINGATE_ASSISTANT" />
          </Element>
        </div>
      )}

      {/* Chatbot Popup */}
      {isOpen && (
        <div
          className="chatbot-popup"
          style={{
            height: isChangeLayout ? 500 : 660,
          }}
          ref={popupRef}
        >
          <div className="chat-main-area">
            <HeaderChatbot toggleChatbot={toggleChatbot} />
            <div className="chatbot-body" ref={chatbotBodyRef}>
              {messageTopic.length === 0 ? (
                <QuestionSuggest
                  sendUserMessage={sendUserMessage}
                  isChangeLayout={isChangeLayout}
                />
              ) : (
                <ContentChat ref={messagesScrollRef} />
              )}
              {statusMess === STATUS_MESS.PENDING && <PendingResponseBubble />}
            </div>
            <FooterChatbot
              question={question}
              setQuestion={setQuestion}
              questionReply={questionReply}
              setQuestionReply={setQuestionReply}
              sendUserMessage={sendUserMessage}
              messageInputRef={messageInputRef}
            />
          </div>
          <TextActionBubble
            isOpen={isOpen}
            setQuestionReply={setQuestionReply}
            messageInputRef={messageInputRef}
            chatbotBodyRef={chatbotBodyRef}
            popupRef={popupRef}
          />
          <HistoryChatbot />
        </div>
      )}
    </div>
  )
}

export default Chatbot
